import React, { useState, useEffect, useCallback } from 'react';
import { orchestratorApi } from '../api/orchestrator';

interface ScanResult {
  id: string;
  entity_id: string;
  scanned_at: string;
  total_scanned: number;
  unanswered_count: number;
  triggered_count: number;
  minutes_threshold: number;
  chats: Array<{
    chatId: string;
    chatName: string;
    phoneNumber: string;
    questions: string[];
    triggered: boolean;
    triggeredAt?: string;
  }>;
}

interface UnansweredArchiveProps {
  entityId: string;
}

const UnansweredArchive: React.FC<UnansweredArchiveProps> = ({ entityId }) => {
  const [scans, setScans] = useState<ScanResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [expandedScanId, setExpandedScanId] = useState<string | null>(null);
  const pageSize = 10;

  const fetchScans = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const headers = await orchestratorApi.getAuthHeaders();
      const res = await fetch(
        `/api/unanswered/entity-assignments/${encodeURIComponent(entityId)}/scan-history?limit=${pageSize}&offset=${page * pageSize}`,
        { headers }
      );
      const data = await res.json();
      if (data.ok) {
        setScans(data.scans);
        setTotal(data.total);
      }
    } catch (e) {
      console.error('Failed to load scan history', e);
    } finally {
      setLoading(false);
    }
  }, [entityId, page]);

  useEffect(() => {
    fetchScans();
  }, [fetchScans]);

  if (loading && scans.length === 0) {
    return <p style={{ color: '#6b7280', padding: '1rem' }}>Loading archive…</p>;
  }

  if (!loading && scans.length === 0) {
    return (
      <div style={{ padding: '1.5rem', textAlign: 'center', color: '#6b7280' }}>
        <p>No scan history yet. Run the Unanswered check from the chat board to start building an archive.</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem' }}>
          {total} scan{total !== 1 ? 's' : ''} on record
        </p>
        <button
          onClick={fetchScans}
          style={{ padding: '4px 12px', fontSize: '0.85rem', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}
        >
          🔄 Refresh
        </button>
      </div>

      {scans.map(scan => {
        const isExpanded = expandedScanId === scan.id;
        const date = new Date(scan.scanned_at);
        const dateStr = date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
        const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        return (
          <div key={scan.id} style={{ border: '1px solid #e5e7eb', borderRadius: '6px', marginBottom: '0.75rem', overflow: 'hidden' }}>
            {/* Scan header row */}
            <div
              onClick={() => setExpandedScanId(isExpanded ? null : scan.id)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '0.75rem 1rem', cursor: 'pointer',
                background: isExpanded ? '#f9fafb' : 'white',
                gap: '1rem'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                <span style={{ fontSize: '0.85rem', color: '#374151', whiteSpace: 'nowrap' }}>
                  <strong>{dateStr}</strong> {timeStr}
                </span>
                <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                  Scanned {scan.total_scanned} chats
                </span>
                <span style={{
                  fontSize: '0.8rem', fontWeight: '600',
                  color: scan.unanswered_count > 0 ? '#dc2626' : '#10b981',
                  background: scan.unanswered_count > 0 ? '#fef2f2' : '#f0fdf4',
                  padding: '2px 8px', borderRadius: '9999px',
                  border: `1px solid ${scan.unanswered_count > 0 ? '#fca5a5' : '#86efac'}`
                }}>
                  {scan.unanswered_count} unanswered
                </span>
                {scan.triggered_count > 0 && (
                  <span style={{
                    fontSize: '0.8rem', fontWeight: '600',
                    color: '#065f46', background: '#d1fae5',
                    padding: '2px 8px', borderRadius: '9999px',
                    border: '1px solid #6ee7b7'
                  }}>
                    ✅ {scan.triggered_count} AI sent
                  </span>
                )}
                <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                  threshold: {scan.minutes_threshold}m
                </span>
              </div>
              <span style={{ color: '#9ca3af', fontSize: '0.85rem' }}>{isExpanded ? '▲' : '▼'}</span>
            </div>

            {/* Expanded chat list */}
            {isExpanded && (
              <div style={{ borderTop: '1px solid #e5e7eb', background: '#f9fafb', padding: '0.75rem 1rem' }}>
                {scan.chats.length === 0 ? (
                  <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: 0 }}>No unanswered chats in this scan.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {scan.chats.map((chat, i) => (
                      <div key={i} style={{
                        display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
                        padding: '0.5rem 0.75rem', background: 'white',
                        border: '1px solid #e5e7eb', borderRadius: '4px'
                      }}>
                        <span style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '2px', minWidth: '16px' }}>{i + 1}.</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                            <strong style={{ fontSize: '0.85rem' }}>{chat.chatName || chat.chatId.split('@')[0]}</strong>
                            {chat.phoneNumber && chat.phoneNumber !== chat.chatId.split('@')[0] && (
                              <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{chat.phoneNumber}</span>
                            )}
                            {chat.triggered ? (
                              <span style={{ fontSize: '0.72rem', color: '#065f46', background: '#d1fae5', padding: '1px 6px', borderRadius: '9999px' }}>
                                ✅ AI sent{chat.triggeredAt ? ` ${new Date(chat.triggeredAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
                              </span>
                            ) : (
                              <span style={{ fontSize: '0.72rem', color: '#92400e', background: '#fef3c7', padding: '1px 6px', borderRadius: '9999px' }}>
                                ❓ Not responded
                              </span>
                            )}
                          </div>
                          {chat.questions && chat.questions.length > 0 && (
                            <p style={{ margin: 0, fontSize: '0.8rem', color: '#374151' }}>
                              {chat.questions[0].length > 200 ? chat.questions[0].substring(0, 200) + '…' : chat.questions[0]}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Pagination */}
      {total > pageSize && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            style={{ padding: '4px 12px', fontSize: '0.85rem', border: '1px solid #d1d5db', borderRadius: '4px', cursor: page === 0 ? 'default' : 'pointer', background: page === 0 ? '#f9fafb' : 'white' }}
          >
            ← Prev
          </button>
          <span style={{ fontSize: '0.85rem', color: '#6b7280', alignSelf: 'center' }}>
            {page + 1} / {Math.ceil(total / pageSize)}
          </span>
          <button
            onClick={() => setPage(p => p + 1)}
            disabled={(page + 1) * pageSize >= total}
            style={{ padding: '4px 12px', fontSize: '0.85rem', border: '1px solid #d1d5db', borderRadius: '4px', cursor: (page + 1) * pageSize >= total ? 'default' : 'pointer', background: (page + 1) * pageSize >= total ? '#f9fafb' : 'white' }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default UnansweredArchive;
